/**
 * Email Settings Page
 * Admin panel for configuring Resend email service
 */

import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Mail, Send, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { emailService } from '../services/email';
import { EmailSettingsFormData, EmailSettings } from '../types/email.types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import toast from 'react-hot-toast';

export default function EmailSettingsPage() {
  const [settings, setSettings] = useState<EmailSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

  const { register, handleSubmit, formState: { errors }, reset, watch } = useForm<EmailSettingsFormData>({
    defaultValues: {
      apiKey: '',
      fromEmail: '',
      fromName: 'CRM Meeting Notes',
      replyToEmail: '',
      enabled: false
    }
  });

  const enabled = watch('enabled');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const data = await emailService.getSettings();
      setSettings(data);

      if (data) {
        reset({
          apiKey: '', // Never load API key from server
          fromEmail: data.fromEmail,
          fromName: data.fromName,
          replyToEmail: data.replyToEmail || '',
          enabled: data.enabled
        });
      }
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Nie udało się załadować ustawień email');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: EmailSettingsFormData) => {
    try {
      setSaving(true);

      // If no API key provided and settings exist, don't send apiKey field
      const payload = { ...data };
      if (!data.apiKey && settings?.hasApiKey) {
        delete payload.apiKey;
      }

      const updatedSettings = await emailService.saveSettings(payload);
      setSettings(updatedSettings);

      toast.success('✅ Ustawienia email zostały zapisane pomyślnie');

      // Clear API key input after save
      reset({
        ...data,
        apiKey: ''
      });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Nie udało się zapisać ustawień');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setTesting(true);
      setTestResult(null);

      const result = await emailService.testConnection();
      setTestResult(result);

      if (result.success) {
        toast.success(`✅ ${result.message}`);
        // Reload settings to update verified status
        await loadSettings();
      } else {
        toast.error(`❌ ${result.message}`);
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || 'Nie udało się przetestować połączenia';
      setTestResult({ success: false, message: errorMessage });
      toast.error(`❌ ${errorMessage}`);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Mail className="w-8 h-8" />
          Ustawienia Email
        </h1>
        <p className="text-muted-foreground mt-2">
          Skonfiguruj wysyłkę emaili z podziękowaniami przez Resend API
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* API Key Section */}
        <Card>
          <CardHeader>
            <CardTitle>🔑 Resend API Key</CardTitle>
            <CardDescription>
              Znajdź swój API key w panelu Resend:{' '}
              <a
                href="https://resend.com/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                https://resend.com/api-keys
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <Label htmlFor="apiKey">
                API Key {settings?.hasApiKey && <span className="text-green-600">(skonfigurowany)</span>}
              </Label>
              <div className="relative">
                <Input
                  id="apiKey"
                  type={showApiKey ? 'text' : 'password'}
                  placeholder={settings?.hasApiKey ? 're_••••••••••••••••••••' : 're_1234567890abcdef'}
                  {...register('apiKey', {
                    required: !settings?.hasApiKey ? 'API Key jest wymagany' : false,
                    pattern: {
                      value: /^re_[a-zA-Z0-9_]+$/,
                      message: 'API Key musi zaczynać się od "re_"'
                    }
                  })}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowApiKey(!showApiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showApiKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.apiKey && (
                <p className="text-sm text-red-600">{errors.apiKey.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                API Key jest szyfrowany przed zapisem w bazie danych
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Sender Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>📧 Konfiguracja Nadawcy</CardTitle>
            <CardDescription>
              Określ dane nadawcy dla wysyłanych emaili
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fromEmail">Email Nadawcy *</Label>
              <Input
                id="fromEmail"
                type="email"
                placeholder="notifications@twojadomena.pl"
                {...register('fromEmail', {
                  required: 'Email nadawcy jest wymagany',
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Nieprawidłowy format email'
                  }
                })}
              />
              {errors.fromEmail && (
                <p className="text-sm text-red-600">{errors.fromEmail.message}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Domena musi być zweryfikowana w Resend
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="fromName">Imię Nadawcy</Label>
              <Input
                id="fromName"
                type="text"
                placeholder="CRM Meeting Notes"
                {...register('fromName')}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="replyToEmail">Email do Odpowiedzi (opcjonalnie)</Label>
              <Input
                id="replyToEmail"
                type="email"
                placeholder="kontakt@twojadomena.pl"
                {...register('replyToEmail', {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: 'Nieprawidłowy format email'
                  }
                })}
              />
              {errors.replyToEmail && (
                <p className="text-sm text-red-600">{errors.replyToEmail.message}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Enable/Disable */}
        <Card>
          <CardHeader>
            <CardTitle>⚙️ Status</CardTitle>
            <CardDescription>
              Włącz lub wyłącz funkcję wysyłki emaili
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="enabled"
                checked={enabled}
                onChange={(e) => {
                  reset({
                    ...watch(),
                    enabled: e.target.checked
                  });
                }}
                className="h-4 w-4"
              />
              <div>
                <Label htmlFor="enabled" className="text-base cursor-pointer">
                  Włącz wysyłkę emaili
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {enabled ? 'Użytkownicy mogą wysyłać emaile z podziękowaniami' : 'Funkcja jest wyłączona'}
                </p>
              </div>
            </div>

            {settings?.verified && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900">Połączenie zweryfikowane</p>
                  <p className="text-xs text-green-700 mt-1">
                    Ostatni test: {settings.lastTestedAt ? new Date(settings.lastTestedAt).toLocaleString('pl-PL') : 'Nigdy'}
                  </p>
                </div>
              </div>
            )}

            {settings && !settings.verified && settings.lastTestedAt && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-start gap-2">
                <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">Weryfikacja nie powiodła się</p>
                  <p className="text-xs text-red-700 mt-1">
                    Ostatni test: {new Date(settings.lastTestedAt).toLocaleString('pl-PL')}
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            type="submit"
            disabled={saving}
            className="flex-1"
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Zapisywanie...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4 mr-2" />
                Zapisz Ustawienia
              </>
            )}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={handleTestConnection}
            disabled={testing || !settings?.hasApiKey}
          >
            {testing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Testowanie...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Testuj Połączenie
              </>
            )}
          </Button>
        </div>

        {testResult && (
          <Card className={testResult.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-2">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600 mt-0.5" />
                )}
                <div>
                  <p className={`text-sm font-medium ${testResult.success ? 'text-green-900' : 'text-red-900'}`}>
                    {testResult.success ? 'Test zakończony sukcesem' : 'Test nie powiódł się'}
                  </p>
                  <p className={`text-xs mt-1 ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.message}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </form>

      {/* Info Card */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-sm">💡 Instrukcja</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>1. Załóż konto na <a href="https://resend.com" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">resend.com</a></p>
          <p>2. Zweryfikuj swoją domenę w panelu Resend</p>
          <p>3. Skopiuj API key i wklej go powyżej</p>
          <p>4. Skonfiguruj dane nadawcy (email musi być z zweryfikowanej domeny)</p>
          <p>5. Przetestuj połączenie przyciskiem "Testuj Połączenie"</p>
          <p>6. Włącz wysyłkę emaili</p>
          <p>7. Teraz użytkownicy mogą wysyłać emaile z podziękowaniami po spotkaniach!</p>
        </CardContent>
      </Card>
    </div>
  );
}
